import React, { useEffect, useState } from "react";
import { useThemeMode } from "../theme/ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";
import Typography from "@mui/material/Typography";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import "./navbar.css";

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [productHovered, setProductHovered] = useState(false);
  const [hamburgerClicked, setHamburgerClicked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { mode: theme, toggleTheme } = useThemeMode();

  function getRole() {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      if (token === "mock-token") return localStorage.getItem("mockRole") || null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.role || null;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    const checkLogin = () => {
      const loggedIn = !!localStorage.getItem("token");
      setIsLoggedIn(loggedIn);
      setIsAdmin(loggedIn && ["admin", "superadmin"].includes(getRole()));
    };

    window.addEventListener("storage", checkLogin);
    window.addEventListener("auth-changed", checkLogin);
    checkLogin();

    return () => {
      window.removeEventListener("storage", checkLogin);
      window.removeEventListener("auth-changed", checkLogin);
    };
  }, []);

  useEffect(() => {
    setProductHovered(false);
    setHamburgerClicked(false);
  }, [location.pathname]);

  const products = [
    "CO₂ and N₂O Liquefaction",
    "CO₂ and N₂O Tank Storage",
    "Heat Pumps",
    "Mushroom Farms Climate Control",
    "Refrigeration for Slaughterhouse",
    "Ice Machines",
    "Process Chillers",
    "Refrigeration for Potato Storage",
    "Dehumidifiers",
    "Waste Heat Recovery",
  ];

  const productRedirects = [
    "co2liquefaction",
    "co2tankstorage",
    "heatpumps",
    "mushroomfarms",
    "slaughterhouse",
    "icemachines",
    "processchillers",
    "coldstores",
    "dehumidifiers",
    "wasteheatrecovery",
  ];

  const goTo = (path) => {
    setProductHovered(false);
    setHamburgerClicked(false);
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.dispatchEvent(new Event("auth-changed"));
    setIsLoggedIn(false);
    setProductHovered(false);
    setHamburgerClicked(false);
    navigate("/");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        className={`navbar-container ${theme === "light" ? "light" : "dark"}`}
        onMouseLeave={() => setProductHovered(false)}
      >
        <div className="navbar">
          <div className="navbar-brand" onClick={() => goTo("/")}>
            <Typography variant="h4" className="title">
              celsius<span className="brand-dot">.</span>in
            </Typography>
          </div>

          <div className="navbar-list">
          <ul className={`navbar-menu ${hamburgerClicked ? "active" : ""}`}>
            <li className="navbar-close-item">
              <button
                className="navbar-close"
                type="button"
                onClick={() => setHamburgerClicked(false)}
                aria-label="Close menu"
              >
                <CloseIcon />
              </button>
            </li>

            <li
              className={`nav-item products ${productHovered ? "active" : ""}`}
              onMouseEnter={() => setProductHovered(true)}
              onClick={() => setProductHovered((prev) => !prev)}
            >
              <span className={location.pathname.startsWith("/product/") ? "nav-active" : ""}>
                Products
              </span>
              {productHovered ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </li>

            {productHovered && products.map((product, index) => (
              <li
                key={index}
                onClick={() => goTo(`/product/${productRedirects[index]}`)}
                className="mobile-products nav-item"
              >
                <span>{product}</span>
              </li>
            ))}

            <li className="nav-item" onClick={() => goTo("/about")}>
              <span className={isActive("/about") ? "nav-active" : ""}>About</span>
            </li>

            <li className="nav-item" onClick={() => goTo("/contactus")}>
              <span className={isActive("/contactus") ? "nav-active" : ""}>Contact Us</span>
            </li>

            {isLoggedIn && (
              <li className="nav-item" onClick={() => goTo("/plc")}>
                <span className={isActive("/plc") ? "nav-active" : ""}>View PLC Data</span>
              </li>
            )}

            {isAdmin && (
              <li className="nav-item" onClick={() => goTo("/admin")}>
                <span className={isActive("/admin") ? "nav-active" : ""}>Admin</span>
              </li>
            )}

            <li className="nav-item theme-toggle-item">
              <button
                type="button"
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label="Toggle dark mode"
                title="Toggle dark mode"
              >
                <span className={`theme-icon-wrap ${theme === "light" ? "active" : ""}`}>
                  <LightModeIcon fontSize="small" />
                </span>
                <span className={`theme-icon-wrap ${theme === "dark" ? "active" : ""}`}>
                  <DarkModeIcon fontSize="small" />
                </span>
              </button>
            </li>

            {!isLoggedIn ? (
              <li className="nav-item auth-pill" onClick={() => goTo("/login")}>
                <span>Login</span>
              </li>
            ) : (
              <li className="nav-item auth-pill" onClick={handleLogout}>
                <span>Logout</span>
              </li>
            )}
          </ul>
          </div>

          <div className="mobile">
            <button
              className="mobile-menu-button"
              type="button"
              onClick={() => setHamburgerClicked(true)}
              aria-label="Open menu"
            >
              <MenuIcon />
            </button>
          </div>
        </div>

        <div className={`product-list ${productHovered ? "hovered" : ""}`}>
          {products.map((product, index) => (
            <button
              key={index}
              type="button"
              onClick={() => goTo(`/product/${productRedirects[index]}`)}
              className="product-link"
            >
              {product}
            </button>
          ))}
        </div>
      </header>

      <div className="navbar-spacer"></div>

      <div
        className={`blur ${hamburgerClicked || productHovered ? "active" : ""}`}
        onClick={() => {
          setHamburgerClicked(false);
          setProductHovered(false);
        }}
      ></div>
    </>
  );
}

export default Navbar;